import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "../lib/theme";
import { formatINR, formatIst } from "../lib/format";

export interface EquityCurvePoint {
  ts: string; // ISO timestamp from the backend
  realized_pnl: string;
  unrealized_pnl: string;
  total_pnl: string;
  is_live: boolean;
}

interface Props {
  points: EquityCurvePoint[];
  height?: number;
}

export default function EquityCurve({ points, height = 280 }: Props) {
  const { resolved } = useTheme();
  const isDark = resolved === "dark";

  if (points.length === 0) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-sm text-muted"
      >
        No P&L data yet. The curve will populate once your deployments take their
        first trades.
      </div>
    );
  }

  const data = points.map((p) => ({
    ts: p.ts,
    total: Number(p.total_pnl),
    realized: Number(p.realized_pnl),
  }));

  const last = data[data.length - 1]!.total;
  const positive = last >= 0;
  const stroke = positive ? "#10b981" : "#f43f5e";
  const grid = isDark ? "#334155" : "#e2e8f0";
  const axis = isDark ? "#94a3b8" : "#64748b";

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="equity-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.4} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="ts"
            stroke={axis}
            fontSize={11}
            tickFormatter={(ts) => formatIst(ts).split(",")[0]!}
            minTickGap={48}
          />
          <YAxis
            stroke={axis}
            fontSize={11}
            tickFormatter={(v: number) =>
              Math.abs(v) >= 1000
                ? `${(v / 1000).toFixed(0)}k`
                : v.toFixed(0)
            }
            width={50}
          />
          <Tooltip
            contentStyle={{
              background: isDark ? "#0b1220" : "#ffffff",
              border: `1px solid ${grid}`,
              borderRadius: 8,
              fontSize: 12,
              color: isDark ? "#f1f5f9" : "#0f172a",
            }}
            labelFormatter={(ts) => formatIst(String(ts))}
            formatter={(v, name) => [
              formatINR(Number(v), { sign: true }),
              String(name) === "total" ? "Total" : "Realized",
            ]}
          />
          <Area
            type="monotone"
            dataKey="total"
            stroke={stroke}
            strokeWidth={2}
            fill="url(#equity-fill)"
            isAnimationActive={false}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
